import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import clientsContactsService from "../services/clientsContactsService";
import "../assets/clientsContacts.module.css";
import { useAuth } from "../contexts/AuthContext";

// Data-protection restriction: only these users may export contacts.
const CONTACTS_EXPORT_ALLOWED_EMAILS = [
  "stuart.woolgar+sm@global-guardians.co.uk",
  "lauren.boswell@global-guardians.co.uk",
  "vikaila.vekaria@global-guardians.co.uk",
];

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const ClientsContacts = () => {
  const { user } = useAuth();
  const canExportContacts =
    !!user?.email &&
    (CONTACTS_EXPORT_ALLOWED_EMAILS.includes(user.email.trim().toLowerCase()) || user.email.trim().toLowerCase().endsWith("@kimonservices.com"));
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);
  const searchTimeout = useRef(null);

  // State for table sorting
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // State for column visibility
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    company: true,
    companyStatus: true,
    companyAddress: true,
    web: true,
    companyPhone: true,
    vipAmount: true,
    savingsMade: true,
    email: true,
    phone: true,
    department: true,
    jobTitle: true,
    reportsTo: true,
    address: true,
    description: true,
    companyInternalInfo: false, // Unchecked by default
    actions: true,
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadContacts();
  }, [currentPage, entriesPerPage]); // Removed searchTerm dependency to prevent constant reloading

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Handle column visibility toggle
  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  // Handle column toggle
  const toggleColumn = (columnKey) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Handle checkbox clicks to prevent dropdown from closing
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking outside the dropdown and not on the card content
      const dropdown = event.target.closest(".column-visibility-dropdown");
      const card = event.target.closest(".panel");
      const table = event.target.closest(".table");
      const controls = event.target.closest(".dt-toolbar");

      if (showColumnVisibility && !dropdown && !card && !table && !controls) {
        setShowColumnVisibility(false);
      }
    };

    if (showColumnVisibility) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnVisibility]);

  const loadContacts = async () => {
    try {
      console.log("🔍 [CLIENTSCONTACTS] loadContacts called with searchTerm:", searchTerm);
      setLoading(true);

      // Call real API only - no mock data fallback
      console.log("🔍 [CLIENTSCONTACTS] Calling real API");
      const response = await clientsContactsService.getContacts({
        draw: 1,
        start: (currentPage - 1) * entriesPerPage,
        length: entriesPerPage,
        search: { value: searchTerm },
      });

      console.log("✅ [CLIENTSCONTACTS] API response received:", response);
      
      // Handle DataTables response format: { draw, recordsTotal, recordsFiltered, data }
      if (response && response.data && Array.isArray(response.data)) {
        console.log("✅ [CLIENTSCONTACTS] DataTables format - data length:", response.data.length);
        setContacts(response.data);
        setTotalRecords(response.recordsFiltered || response.recordsTotal || response.data.length);
      } else if (response && Array.isArray(response)) {
        // Handle direct array response
        console.log("✅ [CLIENTSCONTACTS] Direct array format - length:", response.length);
        setContacts(response);
        setTotalRecords(response.length);
      } else if (response && response.success && response.data && Array.isArray(response.data)) {
        // Handle success wrapper format: { success: true, data: [...] }
        console.log("✅ [CLIENTSCONTACTS] Success wrapper format - data length:", response.data.length);
        setContacts(response.data);
        setTotalRecords(response.data.length);
      } else {
        console.warn("⚠️ [CLIENTSCONTACTS] Unexpected response format:", response);
        // Show empty state instead of mock data
        setContacts([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("❌ [CLIENTSCONTACTS] Error loading contacts:", error);
      // Show empty state instead of mock data
      setContacts([]);
      setTotalRecords(0);
      // Show error toast to user
      toast.error("Failed to load client contacts. Please try again.");
    } finally {
      console.log("✅ [CLIENTSCONTACTS] Setting loading to false");
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    // Reload contacts after clearing search
    setTimeout(() => {
      loadContacts();
    }, 100);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    // Debounce the search to avoid too many API calls
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      loadContacts();
    }, 500);
  };

  const handleExportCSV = () => {
    if (!canExportContacts) return;
    clientsContactsService.exportCSV(contacts);
  };

  const handleExportPDF = () => {
    if (!canExportContacts) return;
    clientsContactsService.exportPDF(contacts);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEditContact = (userId) => {
    navigate(`/users/${userId}/edit`);
  };

  const handleRemoveContact = async (userId) => {
    if (window.confirm("Are you sure you want to remove?")) {
      try {
        await clientsContactsService.deleteContact(userId);
        toast.success("Contact removed successfully");
        loadContacts();
      } catch (error) {
        console.error("Error removing contact:", error);
        toast.error("Failed to remove contact");
      }
    }
  };

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort contacts
  const sortedContacts = React.useMemo(() => {
    return [...contacts].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "name") {
        aValue = a.name || "";
        bValue = b.name || "";
      } else if (sortField === "company") {
        aValue = a.company || "";
        bValue = b.company || "";
      } else if (sortField === "companyStatus") {
        aValue = a.company_status || "";
        bValue = b.company_status || "";
      } else if (sortField === "companyAddress") {
        aValue = a.company_address || "";
        bValue = b.company_address || "";
      } else if (sortField === "web") {
        aValue = a.web || "";
        bValue = b.web || "";
      } else if (sortField === "companyPhone") {
        aValue = a.company_phone || "";
        bValue = b.company_phone || "";
      } else if (sortField === "vipAmount") {
        aValue = parseFloat(a.vip_amount) || 0;
        bValue = parseFloat(b.vip_amount) || 0;
      } else if (sortField === "savingsMade") {
        aValue = parseFloat(a.savings_made) || 0;
        bValue = parseFloat(b.savings_made) || 0;
      } else if (sortField === "email") {
        aValue = a.email || "";
        bValue = b.email || "";
      } else if (sortField === "phone") {
        aValue = a.phone || "";
        bValue = b.phone || "";
      } else if (sortField === "department") {
        aValue = a.department || "";
        bValue = b.department || "";
      } else if (sortField === "jobTitle") {
        aValue = a.job_title || "";
        bValue = b.job_title || "";
      } else if (sortField === "reportsTo") {
        aValue = a.reports_to || "";
        bValue = b.reports_to || "";
      } else if (sortField === "address") {
        aValue = a.address || "";
        bValue = b.address || "";
      } else if (sortField === "description") {
        aValue = a.description || "";
        bValue = b.description || "";
      } else if (sortField === "companyInternalInfo") {
        aValue = a.company_internal_info || "";
        bValue = b.company_internal_info || "";
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [contacts, sortField, sortDirection]);

  const totalPages = Math.ceil(totalRecords / entriesPerPage);
  const startRecord = (currentPage - 1) * entriesPerPage + 1;
  const endRecord = Math.min(currentPage * entriesPerPage, totalRecords);

  return (
    <div className="clients-contacts-container">
      {/* Breadcrumb */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{
            fontWeight: 400,
            fontSize: "14px",
            color: "black",
            cursor: "pointer",
          }}
          onClick={() => navigate("/dashboard")}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{
            fontWeight: 400,
            fontSize: "14px",
            color: "black",
            cursor: "pointer",
          }}
          onClick={() => navigate("/dashboard")}
        >
          / Clients
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">/ Contacts</span>
        </div>
      </div>

      {/* Main Panel */}
      <div className="panel panel-default panel-standard">
        <div className="panel-heading panel-title">
          <h3>Client Contacts</h3>
        </div>
        <div className="panel-body">
          {/* Search Form */}
          <div className="search-form-container">
            <div className="form-inline">
              <div className="form-group">
                <label htmlFor="company" className="sr-only">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  className="form-control"
                  placeholder="Search by company..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <button
                type="button"
                className="btn btn-default"
                onClick={handleClearSearch}
              >
                Clear
              </button>
            </div>
          </div>

          {/* DataTable Toolbar */}
          <div className="dt-toolbar">
            <div className="dt-toolbar-left">
              <div className="dataTables_length">
                <label>
                  Show
                  <select
                    value={entriesPerPage}
                    onChange={(e) =>
                      setEntriesPerPage(parseInt(e.target.value))
                    }
                    className="form-control input-sm"
                  >
                    <option value={30}>30</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={75}>75</option>
                    <option value={100}>100</option>
                  </select>
                  entries
                </label>
              </div>
            </div>
            <div className="dt-toolbar-right">
              <div className="dt-buttons">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={handleExportCSV}
                  style={{ display: canExportContacts ? undefined : "none" }}
                >
                  <i className="glyphicon glyphicon-download"></i> Export as CSV
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={handleExportPDF}
                  style={{ display: canExportContacts ? undefined : "none" }}
                >
                  <i className="glyphicon glyphicon-file"></i> Export as PDF
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={handlePrint}
                >
                  <i className="glyphicon glyphicon-print"></i> Print table
                </button>
                <div
                  className="column-visibility-container"
                  style={{ position: "relative", display: "inline-block" }}
                >
                  <button
                    type="button"
                    className="btn btn-default dropdown-toggle"
                    onClick={handleColumnVisibility}
                  >
                    <i className="glyphicon glyphicon-eye-open"></i> Column
                    visibility
                    <span className="caret"></span>
                  </button>

                  {showColumnVisibility && (
                    <div
                      className="column-visibility-dropdown"
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        minWidth: "200px",
                        padding: "10px",
                        marginTop: "5px",
                      }}
                    >
                      <div style={{ marginBottom: "10px", fontWeight: "bold" }}>
                        Show/Hide Columns
                      </div>
                      {Object.entries(visibleColumns).map(
                        ([columnKey, isVisible]) => (
                          <div key={columnKey} style={{ marginBottom: "5px" }}>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                              onClick={handleCheckboxClick}
                            >
                              <input
                                type="checkbox"
                                checked={isVisible}
                                onChange={() => toggleColumn(columnKey)}
                                style={{ marginRight: "8px" }}
                                onClick={handleCheckboxClick}
                              />
                              <span>
                                {columnKey.charAt(0).toUpperCase() +
                                  columnKey.slice(1).replace(/([A-Z])/g, " $1")}
                              </span>
                            </label>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* DataTable */}
          <div className="dataTable_wrapper">
            <table
              id="datatable-all-client-contacts"
              className="table table-hover dt-responsive"
            >
              <thead>
                <tr>
                  {visibleColumns.name && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Name <SortIcon
                        direction={sortField === "name" ? sortDirection : null}
                        isActive={sortField === "name"}
                      />
                    </th>
                  )}
                  {visibleColumns.company && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("company")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Company <SortIcon
                        direction={
                          sortField === "company" ? sortDirection : null
                        }
                        isActive={sortField === "company"}
                      />
                    </th>
                  )}
                  {visibleColumns.companyStatus && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("companyStatus")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Company status <SortIcon
                        direction={
                          sortField === "companyStatus" ? sortDirection : null
                        }
                        isActive={sortField === "companyStatus"}
                      />
                    </th>
                  )}
                  {visibleColumns.companyAddress && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("companyAddress")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Company Address <SortIcon
                        direction={
                          sortField === "companyAddress" ? sortDirection : null
                        }
                        isActive={sortField === "companyAddress"}
                      />
                    </th>
                  )}
                  {visibleColumns.web && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("web")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Web <SortIcon
                        direction={sortField === "web" ? sortDirection : null}
                        isActive={sortField === "web"}
                      />
                    </th>
                  )}
                  {visibleColumns.companyPhone && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("companyPhone")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Company Phone <SortIcon
                        direction={
                          sortField === "companyPhone" ? sortDirection : null
                        }
                        isActive={sortField === "companyPhone"}
                      />
                    </th>
                  )}
                  {visibleColumns.vipAmount && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("vipAmount")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      VIP Amount <SortIcon
                        direction={
                          sortField === "vipAmount" ? sortDirection : null
                        }
                        isActive={sortField === "vipAmount"}
                      />
                    </th>
                  )}
                  {visibleColumns.savingsMade && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("savingsMade")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Savings made <SortIcon
                        direction={
                          sortField === "savingsMade" ? sortDirection : null
                        }
                        isActive={sortField === "savingsMade"}
                      />
                    </th>
                  )}
                  {visibleColumns.email && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("email")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      E-mail <SortIcon
                        direction={sortField === "email" ? sortDirection : null}
                        isActive={sortField === "email"}
                      />
                    </th>
                  )}
                  {visibleColumns.phone && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("phone")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Phone <SortIcon
                        direction={sortField === "phone" ? sortDirection : null}
                        isActive={sortField === "phone"}
                      />
                    </th>
                  )}
                  {visibleColumns.department && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("department")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Department <SortIcon
                        direction={
                          sortField === "department" ? sortDirection : null
                        }
                        isActive={sortField === "department"}
                      />
                    </th>
                  )}
                  {visibleColumns.jobTitle && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("jobTitle")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Job title <SortIcon
                        direction={
                          sortField === "jobTitle" ? sortDirection : null
                        }
                        isActive={sortField === "jobTitle"}
                      />
                    </th>
                  )}
                  {visibleColumns.reportsTo && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("reportsTo")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Reports to <SortIcon
                        direction={
                          sortField === "reportsTo" ? sortDirection : null
                        }
                        isActive={sortField === "reportsTo"}
                      />
                    </th>
                  )}
                  {visibleColumns.address && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("address")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Address <SortIcon
                        direction={
                          sortField === "address" ? sortDirection : null
                        }
                        isActive={sortField === "address"}
                      />
                    </th>
                  )}
                  {visibleColumns.description && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("description")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Description <SortIcon
                        direction={
                          sortField === "description" ? sortDirection : null
                        }
                        isActive={sortField === "description"}
                      />
                    </th>
                  )}
                  {visibleColumns.companyInternalInfo && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("companyInternalInfo")}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Company internal info <SortIcon
                        direction={
                          sortField === "companyInternalInfo"
                            ? sortDirection
                            : null
                        }
                        isActive={sortField === "companyInternalInfo"}
                      />
                    </th>
                  )}
                  {visibleColumns.actions && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={
                        Object.values(visibleColumns).filter(Boolean).length
                      }
                      className="text-center"
                    >
                      <i className="glyphicon glyphicon-refresh spinning"></i>{" "}
                      Loading...
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        Object.values(visibleColumns).filter(Boolean).length
                      }
                      className="text-center"
                    >
                      No contacts found
                    </td>
                  </tr>
                ) : (
                  sortedContacts.map((contact, index) => (
                    <tr key={contact.contactId || index}>
                      {visibleColumns.name && (
                        <td className="data-export">{contact.name}</td>
                      )}
                      {visibleColumns.company && (
                        <td
                          className="data-export"
                          dangerouslySetInnerHTML={{ __html: contact.company }}
                        ></td>
                      )}
                      {visibleColumns.companyStatus && (
                        <td className="data-export">{contact.clientStatus}</td>
                      )}
                      {visibleColumns.companyAddress && (
                        <td className="data-export">
                          {contact.company_address}
                        </td>
                      )}
                      {visibleColumns.web && (
                        <td className="data-export">{contact.web}</td>
                      )}
                      {visibleColumns.companyPhone && (
                        <td className="data-export">
                          {contact.companyPhoneNumber}
                        </td>
                      )}
                      {visibleColumns.vipAmount && (
                        <td className="data-export">
                          £{contact.vip_amount?.toLocaleString()}
                        </td>
                      )}
                      {visibleColumns.savingsMade && (
                        <td className="data-export">
                          £{contact.savings_made?.toLocaleString()}
                        </td>
                      )}
                      {visibleColumns.email && (
                        <td
                          className="data-export"
                          dangerouslySetInnerHTML={{ __html: contact.email }}
                        ></td>
                      )}
                      {visibleColumns.phone && (
                        <td className="data-export">{contact.phone}</td>
                      )}
                      {visibleColumns.department && (
                        <td className="data-export">{contact.department}</td>
                      )}
                      {visibleColumns.jobTitle && (
                        <td className="data-export">{contact.job_title}</td>
                      )}
                      {visibleColumns.reportsTo && (
                        <td className="data-export">{contact.reports_to}</td>
                      )}
                      {visibleColumns.address && (
                        <td className="data-export">{contact.address}</td>
                      )}
                      {visibleColumns.description && (
                        <td className="data-export">{contact.description}</td>
                      )}
                      {visibleColumns.companyInternalInfo && (
                        <td className="data-export">{contact.internal_info}</td>
                      )}
                      {visibleColumns.actions && (
                        <td className="action">
                          {contact.userId && (
                            <a
                              href={`/users/${contact.userId}/edit`}
                              className="btn btn-sm btn-primary"
                              style={{ marginRight: "8px" }}
                            >
                              Edit
                            </a>
                          )}
                          {contact.userId && (
                            <form
                              method="POST"
                              action={`/clients/contacts/${contact.userId}`}
                              style={{ display: "inline-block" }}
                              onSubmit={(e) => {
                                e.preventDefault();
                                handleRemoveContact(contact.userId);
                              }}
                            >
                              <input
                                type="hidden"
                                name="_method"
                                value="DELETE"
                              />
                              <button
                                type="submit"
                                className="btn btn-sm btn-danger"
                              >
                                Remove
                              </button>
                            </form>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="row">
            <div className="col-sm-5">
              <div className="dataTables_info">
                Showing {startRecord} to {endRecord} of {totalRecords} entries
              </div>
            </div>
            <div className="col-sm-7">
              <div className="dataTables_paginate paging_simple_numbers">
                <ul className="pagination">
                  <li
                    className={`paginate_button previous ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) setCurrentPage(currentPage - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <li
                        key={pageNum}
                        className={`paginate_button ${
                          currentPage === pageNum ? "active" : ""
                        }`}
                      >
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(pageNum);
                          }}
                        >
                          {pageNum}
                        </a>
                      </li>
                    );
                  })}

                  <li
                    className={`paginate_button next ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < totalPages)
                          setCurrentPage(currentPage + 1);
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsContacts;
